import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Store, Save, X, Upload } from 'lucide-react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import MapCoordinatePicker from '../components/MapCoordinatePicker';
import { uploadImage, generateImagePath } from '../services/imageUploadService';

interface Producer {
  id: string;
  name: string;
  description: string;
  category: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  image: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  products: string[];
  specialties: string[];
  openingHours: string;
  socialMedia: {
    facebook?: string;
    instagram?: string;
    whatsapp?: string;
  };
}

const ProducersNew = () => {
  const [producers, setProducers] = useState<Producer[]>([]);
  const [selectedProducer, setSelectedProducer] = useState<Producer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState<Omit<Producer, 'id'>>({
    name: '',
    description: '',
    category: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    image: '',
    coordinates: {
      latitude: 0,
      longitude: 0
    },
    products: [],
    specialties: [],
    openingHours: '',
    socialMedia: {}
  });

  // Dati hardcoded dall'app mobile (da sincronizzare)
  const mobileProducers: Producer[] = [
    {
      id: '1',
      name: 'Azienda Agricola Cilento Verde',
      description: 'Produttore di olio extravergine di oliva biologico e prodotti tipici del Cilento. Famiglia di agricoltori da tre generazioni, coltivano le proprie olive con metodi tradizionali e sostenibili.',
      category: 'Agricoltura',
      address: 'Via delle Olive, 12 - Casal Velino (SA)',
      phone: '+39 0974 123456',
      email: 'info@cilentoverde.it',
      website: 'www.cilentoverde.it',
      image: '/placeholder-producer.jpg',
      coordinates: {
        latitude: 40.1691,
        longitude: 15.1430
      },
      products: ['Olio EVO', 'Olio biologico', 'Olio aromatizzato'],
      specialties: ['Olio extravergine di oliva', 'Prodotti biologici', 'Tradizione familiare'],
      openingHours: 'Lun-Ven: 8:00-18:00, Sab: 8:00-13:00',
      socialMedia: {
        facebook: 'https://facebook.com/cilentoverde',
        instagram: '@cilentoverde'
      }
    },
    {
      id: '2',
      name: 'Caseificio del Cilento',
      description: 'Specializzato nella produzione di formaggi tipici del Cilento, tra cui il famoso Cacioricotta. Utilizzano solo latte di mucche allevate al pascolo.',
      category: 'Caseificio',
      address: 'Contrada San Marco, 8 - Casal Velino (SA)',
      phone: '+39 0974 234567',
      email: 'info@caseificiodelcilento.it',
      website: 'www.caseificiodelcilento.it',
      image: '/placeholder-producer.jpg',
      coordinates: {
        latitude: 40.1685,
        longitude: 15.1415
      },
      products: ['Cacioricotta', 'Mozzarella', 'Ricotta', 'Pecorino'],
      specialties: ['Formaggi tradizionali', 'Latte di pascolo', 'Tecniche antiche'],
      openingHours: 'Lun-Sab: 6:00-20:00',
      socialMedia: {
        instagram: '@caseificiodelcilento'
      }
    },
    {
      id: '3',
      name: 'Cantina del Cilento',
      description: 'Cantina storica che produce vini DOC del Cilento. Vigneti situati sulle colline che si affacciano sul mare, con una vista mozzafiato.',
      category: 'Cantina',
      address: 'Via dei Vigneti, 25 - Casal Velino (SA)',
      phone: '+39 0974 345678',
      email: 'info@cantinadelcilento.it',
      website: 'www.cantinadelcilento.it',
      image: '/placeholder-producer.jpg',
      coordinates: {
        latitude: 40.1680,
        longitude: 15.1400
      },
      products: ['Aglianico', 'Fiano', 'Greco di Tufo', 'Rosato'],
      specialties: ['Vini DOC', 'Vigneti marini', 'Degustazioni'],
      openingHours: 'Lun-Sab: 9:00-19:00, Dom: 10:00-18:00',
      socialMedia: {
        facebook: 'https://facebook.com/cantinadelcilento',
        instagram: '@cantinadelcilento'
      }
    },
    {
      id: '4',
      name: 'Panificio Tradizionale del Borgo',
      description: 'Panificio di famiglia che produce pane e prodotti da forno secondo le antiche ricette del Cilento. Forno a legna tradizionale.',
      category: 'Panificio',
      address: 'Piazza Lista, 3 - Casal Velino (SA)',
      phone: '+39 0974 456789',
      email: 'info@panificiodelborgo.it',
      website: 'www.panificiodelborgo.it',
      image: '/placeholder-producer.jpg',
      coordinates: {
        latitude: 40.1689,
        longitude: 15.1428
      },
      products: ['Pane del Cilento', 'Focacce', 'Biscotti', 'Pizza'],
      specialties: ['Forno a legna', 'Ricette antiche', 'Prodotti artigianali'],
      openingHours: 'Lun-Sab: 6:00-20:00, Dom: 7:00-13:00',
      socialMedia: {
        instagram: '@panificiodelborgo'
      }
    },
    {
      id: '5',
      name: 'Azienda Agricola Biologica Cilento',
      description: 'Produttore di ortaggi biologici e erbe aromatiche. Coltivazione sostenibile e rispettosa dell\'ambiente.',
      category: 'Agricoltura',
      address: 'Contrada San Giuseppe, 15 - Casal Velino (SA)',
      phone: '+39 0974 567890',
      email: 'info@cilentobio.it',
      website: 'www.cilentobio.it',
      image: '/placeholder-producer.jpg',
      coordinates: {
        latitude: 40.1695,
        longitude: 15.1435
      },
      products: ['Ortaggi biologici', 'Erbe aromatiche', 'Miele', 'Confetture'],
      specialties: ['Agricoltura biologica', 'Prodotti stagionali', 'Sostenibilità'],
      openingHours: 'Lun-Ven: 8:00-17:00, Sab: 8:00-13:00',
      socialMedia: {
        facebook: 'https://facebook.com/cilentobio',
        instagram: '@cilentobio'
      }
    },
    {
      id: '6',
      name: 'Laboratorio Artigianale del Cilento',
      description: 'Laboratorio che produce ceramiche artistiche e oggetti decorativi ispirati alla tradizione cilentana.',
      category: 'Artigianato',
      address: 'Via dell\'Artigianato, 7 - Casal Velino (SA)',
      phone: '+39 0974 678901',
      email: 'info@artigianatocilento.it',
      website: 'www.artigianatocilento.it',
      image: '/placeholder-producer.jpg',
      coordinates: {
        latitude: 40.1682,
        longitude: 15.1420
      },
      products: ['Ceramiche artistiche', 'Oggetti decorativi', 'Gioielli', 'Souvenir'],
      specialties: ['Ceramica tradizionale', 'Design artistico', 'Lavorazione manuale'],
      openingHours: 'Lun-Sab: 9:00-18:00',
      socialMedia: {
        instagram: '@artigianatocilento',
        facebook: 'https://facebook.com/artigianatocilento'
      }
    }
  ];

  const categories = [
    'Agricoltura',
    'Caseificio',
    'Cantina',
    'Panificio',
    'Artigianato',
    'Ristorazione',
    'Turismo',
    'Servizi'
  ];

  useEffect(() => {
    loadProducers();
  }, []);

  const loadProducers = async () => {
    try {
      setIsLoading(true);
      const producersCol = collection(db, 'producers');
      const snapshot = await getDocs(producersCol);
      const producersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Producer[];
      
      // Se non ci sono produttori nel database, usa quelli hardcoded
      if (producersData.length === 0) {
        console.log('Nessun produttore trovato in Firestore, usando dati hardcoded');
        setProducers(mobileProducers);
      } else {
        console.log(`Caricati ${producersData.length} produttori da Firestore`);
        setProducers(producersData);
      }
    } catch (error) {
      console.error('Error loading producers:', error);
      // Fallback ai produttori hardcoded
      setProducers(mobileProducers);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectProducer = (producer: Producer) => {
    setSelectedProducer(producer);
    setFormData({
      name: producer.name,
      description: producer.description,
      category: producer.category,
      address: producer.address,
      phone: producer.phone,
      email: producer.email,
      website: producer.website,
      image: producer.image,
      coordinates: producer.coordinates,
      products: producer.products,
      specialties: producer.specialties,
      openingHours: producer.openingHours,
      socialMedia: producer.socialMedia
    });
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleNew = () => {
    setSelectedProducer(null);
    setFormData({
      name: '',
      description: '',
      category: '',
      address: '',
      phone: '',
      email: '',
      website: '',
      image: '',
      coordinates: {
        latitude: 0,
        longitude: 0
      },
      products: [],
      specialties: [],
      openingHours: '',
      socialMedia: {}
    });
    setIsEditing(true);
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const producerId = selectedProducer?.id || `temp_${Date.now()}`;
      const imagePath = generateImagePath('producers', producerId, file.name);
      const imageUrl = await uploadImage(file, imagePath);
      setFormData(prev => ({ ...prev, image: imageUrl }));
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Errore durante il caricamento dell\'immagine');
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handleSave = async () => {
    try {
      if (selectedProducer) {
        await updateDoc(doc(db, 'producers', selectedProducer.id), formData);
        // Aggiorna la lista locale
        setProducers(prev => prev.map(p => p.id === selectedProducer.id ? { ...p, ...formData } : p));
        setSelectedProducer({ ...selectedProducer, ...formData });
      } else {
        const newProducer = await addDoc(collection(db, 'producers'), formData);
        const newProd = { id: newProducer.id, ...formData };
        setProducers(prev => [...prev, newProd]);
        setSelectedProducer(newProd);
      }
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving producer:', error);
    }
  };

  const handleCancel = () => {
    if (selectedProducer) {
      setFormData({
        name: selectedProducer.name,
        description: selectedProducer.description,
        category: selectedProducer.category,
        address: selectedProducer.address,
        phone: selectedProducer.phone,
        email: selectedProducer.email,
        website: selectedProducer.website,
        image: selectedProducer.image,
        coordinates: selectedProducer.coordinates,
        products: selectedProducer.products,
        specialties: selectedProducer.specialties,
        openingHours: selectedProducer.openingHours,
        socialMedia: selectedProducer.socialMedia
      });
    }
    setIsEditing(false);
  };

  const handleDelete = async (producerId: string) => {
    if (window.confirm('Sei sicuro di voler eliminare questo produttore?')) {
      try {
        await deleteDoc(doc(db, 'producers', producerId));
        setProducers(prev => prev.filter(p => p.id !== producerId));
        if (selectedProducer?.id === producerId) {
          setSelectedProducer(null);
        }
      } catch (error) {
        console.error('Error deleting producer:', error);
      }
    }
  };


  const addItem = (type: 'products' | 'specialties', item: string) => {
    if (item.trim() && !formData[type].includes(item.trim())) {
      setFormData({
        ...formData,
        [type]: [...formData[type], item.trim()]
      });
    }
  };

  const removeItem = (type: 'products' | 'specialties', item: string) => {
    setFormData({
      ...formData,
      [type]: formData[type].filter(i => i !== item)
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-primary text-white p-6">
        <div className="container-max">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Store size={24} />
              <h1 className="text-2xl font-bold">Gestione Produttori</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleNew}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
              >
                <Plus size={16} />
                <span>Nuovo Produttore</span>
              </button>
            </div>
          </div>
          <p className="text-white/80 mt-2">
            Gestisci i produttori locali e le aziende del territorio
          </p>
        </div>
      </div>

      <div className="container-max p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lista Produttori */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-border">
              <div className="p-4 border-b border-border">
                <h2 className="text-lg font-semibold text-text-primary">Produttori Disponibili</h2>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {isLoading ? (
                  <div className="p-4 space-y-3">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} className="flex items-center space-x-3 p-3 animate-pulse">
                        <div className="w-16 h-16 bg-gray-200 rounded-lg"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 rounded mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-2">
                    {producers.map((producer) => (
                      <div
                        key={producer.id}
                        onClick={() => handleSelectProducer(producer)}
                        className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors duration-200 ${
                          selectedProducer?.id === producer.id
                            ? 'bg-primary/10 border border-primary/20'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden">
                          <img 
                            src={producer.image || '/placeholder-producer.jpg'} 
                            alt={producer.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-text-primary truncate">
                            {producer.name}
                          </h3>
                          <p className="text-sm text-text-secondary truncate">
                            {producer.category}
                          </p>
                          <p className="text-xs text-text-secondary truncate">
                            {producer.address}
                          </p>
                        </div>
                        <div className="flex space-x-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectProducer(producer);
                              handleEdit();
                            }}
                            className="p-1 text-text-secondary hover:text-primary transition-colors duration-200"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(producer.id);
                            }}
                            className="p-1 text-text-secondary hover:text-error transition-colors duration-200"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Form di Editing */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-border">
              {selectedProducer ? (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden">
                        <img 
                          src={selectedProducer.image || '/placeholder-producer.jpg'} 
                          alt={selectedProducer.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-text-primary">
                          {selectedProducer.name}
                        </h2>
                        <p className="text-text-secondary">
                          {selectedProducer.category}
                        </p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      {isEditing ? (
                        <>
                          <button
                            onClick={handleSave}
                            className="flex items-center space-x-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors duration-200"
                          >
                            <Save size={16} />
                            <span>Salva</span>
                          </button>
                          <button
                            onClick={handleCancel}
                            className="flex items-center space-x-2 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors duration-200"
                          >
                            <X size={16} />
                            <span>Annulla</span>
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={handleEdit}
                          className="flex items-center space-x-2 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary/90 transition-colors duration-200"
                        >
                          <Edit size={16} />
                          <span>Modifica</span>
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-6">
                    {/* Informazioni Base */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Nome Azienda *
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Categoria *
                        </label>
                        <select
                          value={formData.category}
                          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                        >
                          <option value="">Seleziona categoria</option>
                          {categories.map((cat) => (
                            <option key={cat} value={cat}>
                              {cat}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Descrizione *
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        disabled={!isEditing}
                        rows={4}
                        className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>

                    {/* Contatti */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Indirizzo *
                        </label>
                        <input
                          type="text"
                          value={formData.address}
                          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Telefono
                        </label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Sito Web
                        </label>
                        <input
                          type="url"
                          value={formData.website}
                          onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </div>
                    </div>

                    {/* Orari */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Orari di Apertura
                      </label>
                      <input
                        type="text"
                        value={formData.openingHours}
                        onChange={(e) => setFormData({ ...formData, openingHours: e.target.value })}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                        placeholder="es. Lun-Ven: 8:00-18:00, Sab: 8:00-13:00"
                      />
                    </div>

                    {/* Immagine */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Immagine
                      </label>
                      <div className="flex items-center space-x-4">
                        <div className="w-24 h-24 bg-gray-200 rounded-lg overflow-hidden">
                          <img 
                            src={formData.image || '/placeholder-producer.jpg'} 
                            alt="Preview"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1">
                          <input
                            type="url"
                            value={formData.image}
                            onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                            disabled={!isEditing}
                            className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                            placeholder="URL dell'immagine"
                          />
                          {isEditing && (
                            <>
                              <input
                                id="producer-image-upload"
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                className="hidden"
                              />
                              <label
                                htmlFor="producer-image-upload"
                                aria-disabled={isUploading}
                                className={`mt-2 flex items-center space-x-2 transition-colors duration-200 ${
                                  isUploading
                                    ? 'text-primary/50 pointer-events-none'
                                    : 'text-primary hover:text-secondary cursor-pointer'
                                }`}
                              >
                                <Upload size={16} />
                                <span className="text-sm">
                                  {isUploading ? 'Caricamento...' : 'Carica immagine'}
                                </span>
                              </label>
                            </>
                          )}
                          {!isEditing && (
                            <button
                              type="button"
                              disabled
                              className="mt-2 flex items-center space-x-2 text-primary/40 cursor-not-allowed"
                            >
                              <Upload size={16} />
                              <span className="text-sm">Carica immagine</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Prodotti */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Prodotti
                      </label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {formData.products.map((product, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-primary/20 text-primary text-sm rounded-full flex items-center space-x-2"
                          >
                            <span>{product}</span>
                            {isEditing && (
                              <button
                                onClick={() => removeItem('products', product)}
                                className="text-primary hover:text-error"
                              >
                                ✕
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                      {isEditing && (
                        <div className="flex space-x-2">
                          <input
                            type="text"
                            placeholder="Aggiungi prodotto"
                            className="flex-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addItem('products', e.currentTarget.value);
                                e.currentTarget.value = '';
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={(e) => {
                              const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                              addItem('products', input.value);
                              input.value = '';
                            }}
                            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                          >
                            Aggiungi
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Specialità */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Specialità
                      </label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {formData.specialties.map((specialty, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-secondary/20 text-secondary text-sm rounded-full flex items-center space-x-2"
                          >
                            <span>{specialty}</span>
                            {isEditing && (
                              <button
                                onClick={() => removeItem('specialties', specialty)}
                                className="text-secondary hover:text-error"
                              >
                                ✕
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                      {isEditing && (
                        <div className="flex space-x-2">
                          <input
                            type="text"
                            placeholder="Aggiungi specialità"
                            className="flex-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addItem('specialties', e.currentTarget.value);
                                e.currentTarget.value = '';
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={(e) => {
                              const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                              addItem('specialties', input.value);
                              input.value = '';
                            }}
                            className="px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90"
                          >
                            Aggiungi
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Social Media */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Social Media
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs text-text-secondary mb-1">
                            Facebook
                          </label>
                          <input
                            type="url"
                            value={formData.socialMedia.facebook || ''}
                            onChange={(e) => setFormData({ 
                              ...formData, 
                              socialMedia: { 
                                ...formData.socialMedia, 
                                facebook: e.target.value 
                              } 
                            })}
                            disabled={!isEditing}
                            className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                            placeholder="https://facebook.com/..."
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-text-secondary mb-1">
                            Instagram
                          </label>
                          <input
                            type="text"
                            value={formData.socialMedia.instagram || ''}
                            onChange={(e) => setFormData({ 
                              ...formData, 
                              socialMedia: { 
                                ...formData.socialMedia, 
                                instagram: e.target.value 
                              } 
                            })}
                            disabled={!isEditing}
                            className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                            placeholder="@username"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-text-secondary mb-1">
                            WhatsApp
                          </label>
                          <input
                            type="tel"
                            value={formData.socialMedia.whatsapp || ''}
                            onChange={(e) => setFormData({ 
                              ...formData, 
                              socialMedia: { 
                                ...formData.socialMedia, 
                                whatsapp: e.target.value 
                              } 
                            })}
                            disabled={!isEditing}
                            className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                            placeholder="+39 123 456 7890"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Coordinate con Google Maps */}
                    <MapCoordinatePicker
                      latitude={formData.coordinates.latitude}
                      longitude={formData.coordinates.longitude}
                      onChange={(lat, lng) => setFormData({
                        ...formData,
                        coordinates: { latitude: lat, longitude: lng }
                      })}
                      disabled={!isEditing}
                    />
                  </div>
                </div>
              ) : (
                <div className="p-12 text-center">
                  <Store size={48} className="mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-text-primary mb-2">
                    Seleziona un Produttore
                  </h3>
                  <p className="text-text-secondary">
                    Scegli un produttore dalla lista per modificarlo o creane uno nuovo
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProducersNew;
